<template>
  <!-- Loading overlay -->
  <div v-if="isLoading" class="saving-overlay">
    <div class="saving-card">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="spin-icon"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-dasharray="40" stroke-dashoffset="15" stroke-linecap="round"/></svg>
      Carregando…
    </div>
  </div>

  <!-- Saving overlay -->
  <div v-if="isSaving" class="saving-overlay">
    <div class="saving-card">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="spin-icon"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-dasharray="40" stroke-dashoffset="15" stroke-linecap="round"/></svg>
      Salvando dados…
    </div>
  </div>

  <div class="sfv-page" :class="{ embedded }">

    <!-- Top bar (standalone only) -->
    <header v-if="!embedded" class="sfv-topbar">
      <div class="sfv-topbar-left">
        <button class="sfv-topbar-back" type="button" @click="cancel">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          Voltar
        </button>
        <div class="sfv-topbar-title">
          {{ isEdit ? 'Editar Relatório' : 'Novo Relatório' }}
          <span v-if="attraction"> · {{ attraction }}</span>
        </div>
      </div>
      <button class="sfv-topbar-esc" type="button" @click="cancel">Esc para cancelar</button>
    </header>

    <!-- Context bar (embedded only — sits ABOVE sfv-body so it never scrolls away) -->
    <div v-if="embedded" class="sfv-context-bar">
      <svg width="14" height="14" viewBox="0 0 18 18" fill="none"><rect x="3" y="3" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M6 7h6M6 9.5h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
      <span class="sfv-context-type">{{ reportTypeLabel || (isEdit ? 'Editar Relatório' : 'Novo Relatório') }}</span>
      <template v-if="attraction">
        <span class="sfv-context-sep">·</span>
        <span class="sfv-context-project">{{ attraction }}</span>
      </template>
      <template v-if="reportDate">
        <span class="sfv-context-sep">·</span>
        <span class="sfv-context-date">{{ reportDate.split('-').reverse().join('/') }}</span>
      </template>
    </div>

    <div class="sfv-body">

      <form class="sfv-form" @submit.prevent>

        <!-- ── 1. Tipo de Relatório ── -->
        <div class="sfv-section type-section">
          <button type="button" class="type-picker-trigger" @click="typePickerOpen = !typePickerOpen">
            <span class="type-picker-label">
              Tipo de relatório:
              <strong>{{ reportTypeLabel || 'Selecione' }}</strong>
            </span>
            <svg class="type-picker-chevron" :class="{ open: typePickerOpen }" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

          <transition name="type-expand">
            <div v-if="typePickerOpen" class="type-grid-wrap">
              <div class="type-grid">

                <label class="type-card" :class="{ active: reportType === 'Relatorio diario de obra' }">
                  <input type="radio" name="reportType" value="Relatorio diario de obra" v-model="reportType" class="sr-only" @change="typePickerOpen = false" />
                  <div class="type-check">✓</div>
                  <div class="type-icon type-icon--daily">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="3" y="3" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M6 7h6M6 9.5h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                  </div>
                  <div class="type-name">Diário</div>
                  <div class="type-desc">Registro diário de obra</div>
                </label>

                <label class="type-card" :class="{ active: reportType === 'Relatorio de incidente' }">
                  <input type="radio" name="reportType" value="Relatorio de incidente" v-model="reportType" class="sr-only" @change="typePickerOpen = false" />
                  <div class="type-check">✓</div>
                  <div class="type-icon type-icon--incident">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 5v4M9 12h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.5"/></svg>
                  </div>
                  <div class="type-name">Incidente</div>
                  <div class="type-desc">Ocorrência ou anomalia</div>
                </label>

                <label class="type-card" :class="{ active: reportType === 'Relatorio geral' }">
                  <input type="radio" name="reportType" value="Relatorio geral" v-model="reportType" class="sr-only" @change="typePickerOpen = false" />
                  <div class="type-check">✓</div>
                  <div class="type-icon type-icon--general">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="3" y="4" width="12" height="10" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M6 8h6M6 10.5h3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                  </div>
                  <div class="type-name">Geral</div>
                  <div class="type-desc">Relatório de uso geral</div>
                </label>

              </div>
            </div>
          </transition>
        </div>

        <!-- ── 2. Dados Gerais ── -->
        <div class="sfv-section">
          <div class="section-head">
            <div class="section-label">Dados Gerais</div>
          </div>
          <div class="fields-row-3">
            <div class="field">
              <label for="attraction">Projeto</label>
              <select id="attraction" v-model="attraction">
                <option v-for="item in projectOptions" :key="item" :value="item">{{ item }}</option>
              </select>
            </div>
            <div class="field">
              <label for="reportDate">Data</label>
              <input id="reportDate" v-model="reportDate" type="date" />
            </div>
            <div class="field">
              <label>Idioma</label>
              <div class="lang-select-wrap" ref="langDropdownRef">
                <button type="button" class="lang-select-trigger" @click="showLangDropdown = !showLangDropdown">
                  <span class="lang-btn-flag">{{ REPORT_LANGUAGES.find(l => l.code === reportLanguage)?.flag }}</span>
                  <span class="lang-select-label">{{ REPORT_LANGUAGES.find(l => l.code === reportLanguage)?.label }}</span>
                  <svg class="lang-select-chevron" :class="{ open: showLangDropdown }" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
                <div v-if="showLangDropdown" class="lang-select-dropdown">
                  <button
                    v-for="loc in REPORT_LANGUAGES"
                    :key="loc.code"
                    type="button"
                    class="lang-select-option"
                    :class="{ active: reportLanguage === loc.code }"
                    @click="reportLanguage = loc.code; showLangDropdown = false"
                  >
                    <span class="lang-btn-flag">{{ loc.flag }}</span>
                    <span>{{ loc.label }}</span>
                    <svg v-if="reportLanguage === loc.code" class="lang-check" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="isAdminUser && !isEdit" class="field" style="margin-top:14px;">
            <label for="report-author">Criar em nome de</label>
            <select id="report-author" v-model="createdOnBehalfOfUsername">
              <option v-for="user in authorOptions" :key="user.username" :value="user.username">
                {{ user.name }} ({{ user.username }})
              </option>
            </select>
          </div>
        </div>

        <!-- ── Alerta de duplicado ── -->
        <div v-if="duplicateSubmission && !suppressDuplicateWarning" class="alert alert-warn">
          <div class="alert-icon">!</div>
          <div class="alert-body">
            Já existe um <strong>{{ reportTypeLabel }}</strong> para <strong>{{ attraction }}</strong> nesta data.
          </div>
          <div class="alert-actions">
            <button class="alert-btn" type="button" @click="editExistingReport">Editar existente</button>
            <button class="alert-btn alert-btn--fill" type="button" @click="suppressDuplicateWarning = true">Continuar</button>
          </div>
        </div>

        <!-- ── Copiar último relatório ── -->
        <div v-if="!isEdit && reportType === 'Relatorio diario de obra'" class="copy-banner">
          <svg width="14" height="14" viewBox="0 0 15 15" fill="none"><rect x="4" y="1" width="9" height="12" rx="1.5" stroke="currentColor" stroke-width="1.3"/><rect x="2" y="3" width="9" height="12" rx="1.5" stroke="currentColor" stroke-width="1.3" fill="var(--bg,#faf9f6)"/><path d="M5 7h5M5 9.5h3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
          <span>Preencher com o último relatório</span>
          <button class="copy-banner-btn" type="button" @click="copyFromLatestDailyReport">Copiar dados</button>
        </div>

        <!-- ── 3. Colaboradores ── -->
        <div class="sfv-section list-section">
          <div class="section-head">
            <div class="section-label">Colaboradores Presentes</div>
            <button class="btn-inline" type="button" @click="addCollaborator">+ Adicionar</button>
          </div>
          <div v-if="collaborators.length === 0" class="empty-state">Nenhum colaborador adicionado</div>
          <div v-else class="list-items">
            <div v-for="(item, index) in collaborators" :key="`colab-${index}`" class="list-item">
              <div class="list-item-avatar">{{ getInitials(collaborators[index]) }}</div>
              <input class="list-item-input" v-model="collaborators[index]" type="text" placeholder="Nome ou função" />
              <button class="list-item-remove" type="button" @click="removeCollaborator(index)" title="Remover" aria-label="Remover">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6h8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
              </button>
            </div>
          </div>
        </div>

        <!-- ── 4. Co-autores ── -->
        <div class="sfv-section list-section">
          <div class="section-head no-mb">
            <div class="section-label">Co-autores</div>
          </div>
          <div class="coauthor-area">
            <!-- Selected chips -->
            <span
              v-for="username in coAuthors"
              :key="username"
              class="coauthor-chip"
            >
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="6" r="3" stroke="currentColor" stroke-width="1.5"/><path d="M2 14c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              {{ resolveCoAuthorName(username) }}
              <button type="button" class="coauthor-chip-rm" @click="removeCoAuthor(username)" title="Remover">
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2L2 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              </button>
            </span>

            <!-- Picker -->
            <div class="coauthor-picker-wrap" ref="coAuthorPickerRef" v-if="coAuthorPickerOptions.length > 0">
              <button type="button" class="btn-inline" @click="coAuthorPickerOpen = !coAuthorPickerOpen">+ Adicionar</button>
              <div v-if="coAuthorPickerOpen" class="coauthor-dropdown">
                <input
                  v-model="coAuthorSearch"
                  class="coauthor-search"
                  type="text"
                  placeholder="Buscar usuário…"
                  autofocus
                />
                <div class="coauthor-dropdown-list">
                  <button
                    v-for="user in filteredCoAuthorOptions"
                    :key="user.username"
                    type="button"
                    class="coauthor-dropdown-item"
                    @click="addCoAuthor(user.username)"
                  >
                    <span class="coauthor-dropdown-name">{{ user.name }}</span>
                    <span class="coauthor-dropdown-user">@{{ user.username }}</span>
                  </button>
                  <div v-if="filteredCoAuthorOptions.length === 0" class="coauthor-dropdown-empty">
                    Nenhum usuário encontrado
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty state (no options at all) -->
            <span v-if="coAuthors.length === 0 && coAuthorPickerOptions.length === 0" class="empty-state">
              Nenhum usuário disponível
            </span>
          </div>
        </div>

        <!-- ── 5. Maquinário ── -->
        <div class="sfv-section expandable-section">
          <div class="section-head">
            <div class="section-label">Maquinário Utilizado</div>
            <button class="btn-inline" type="button" @click="addTool">+ Adicionar</button>
          </div>
          <div class="expandable-list" v-if="tools.length > 0">
            <div v-for="(section, index) in tools" :key="`tool-${section.id}`"
              class="item-row-wrap" :class="{ 'item-row-wrap--removing': deletingId === section.id }">
            <div class="item-row"
              :class="{ 'item-row--drag-over': dragSrcType === 'tools' && dragOverItemIdx === index && dragSrcIdx !== index }"
              draggable="true"
              @dragstart="onItemDragStart(index, 'tools', $event)"
              @dragover.prevent="onItemDragOver(index, 'tools')"
              @drop.prevent="onItemDrop(index, 'tools')"
              @dragend="onItemDragEnd"
            >
              <div class="drag-handle" title="Arrastar para reordenar">
                <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
                  <circle cx="3" cy="3" r="1.4" fill="currentColor"/><circle cx="7" cy="3" r="1.4" fill="currentColor"/>
                  <circle cx="3" cy="7" r="1.4" fill="currentColor"/><circle cx="7" cy="7" r="1.4" fill="currentColor"/>
                  <circle cx="3" cy="11" r="1.4" fill="currentColor"/><circle cx="7" cy="11" r="1.4" fill="currentColor"/>
                </svg>
              </div>
              <div class="flip-card" :class="{ 'flip-card--flipped': deletePending?.type === 'tools' && deletePending.index === index }">
                <!-- back face (delete confirm) -->
                <div class="flip-card-back">
                  <p class="flip-confirm-label">Remover maquinário {{ index + 1 }}?</p>
                  <div class="flip-confirm-actions">
                    <button class="flip-btn flip-btn--yes" type="button" @click="confirmDelete">Sim, remover</button>
                    <button class="flip-btn flip-btn--no" type="button" @click="cancelDelete">Não</button>
                  </div>
                </div>
                <!-- front face -->
                <div class="flip-card-front expandable-item">
                <div class="expandable-item-header">
                  <div class="item-badge">{{ index + 1 }}</div>
                  <span class="item-title">{{ getMachineTitle(section.text) || `Maquinário ${index + 1}` }}</span>
                  <button class="item-remove-btn" type="button" @click="askDelete('tools', index)" title="Remover">
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 3.5h10M5.5 3.5V2.5h3V3.5M4.5 3.5v7.5h5V3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    Remover
                  </button>
                </div>
                <div class="field" style="margin-bottom:14px;">
                  <label :for="`tool-text-${section.id}`">Maquinário</label>
                  <select :id="`tool-text-${section.id}`" v-model="tools[index].text">
                    <option value="">Selecione o maquinário</option>
                    <option v-for="m in machineryOptions" :key="m.id" :value="m.id">{{ m.title }}</option>
                  </select>
                </div>
                <div class="field">
                  <label>Fotos <span class="img-count">{{ section.images.length }}/25</span></label>
                  <div class="image-grid" :class="{ 'is-drag-over': dragOverToolId === section.id }"
                    @dragenter.prevent="dragOverToolId = section.id"
                    @dragover.prevent="dragOverToolId = section.id"
                    @dragleave.prevent="dragOverToolId = dragOverToolId === section.id ? '' : dragOverToolId"
                    @drop.prevent="onToolImagesDropped(section.id, $event)"
                  >
                    <div v-for="(img, imgIndex) in section.images" :key="`tool-${section.id}-img-${imgIndex}`" class="image-preview">
                      <img :src="resolveUrl(img)" alt="preview" />
                      <button class="image-remove" type="button" @click="removeToolImage(section.id, imgIndex)" aria-label="Remover foto">
                        <span class="trash-icon" aria-hidden="true"></span>
                      </button>
                    </div>
                    <button v-if="section.images.length < 25" class="image-add" type="button" @click="triggerToolImageInput(section.id)">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 4v10M4 9h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                    </button>
                  </div>
                  <input :id="`file-tool-${section.id}`" class="hidden-input" type="file" accept="image/*" multiple @change="onToolImagesSelected(section.id, $event)" />
                </div>
                </div><!-- /flip-card-front -->
              </div><!-- /flip-card -->
            </div><!-- /item-row -->
            </div><!-- /item-row-wrap -->
          </div>
          <button class="btn-add-bottom" type="button" @click="addTool">
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 3v8M3 7h8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            Adicionar maquinário
          </button>
        </div>

        <!-- ── 6. Atividades ── -->
        <div class="sfv-section expandable-section">
          <div class="section-head">
            <div class="section-label">Atividades Executadas</div>
            <button class="btn-inline" type="button" @click="addActivity">+ Adicionar</button>
          </div>
          <div class="expandable-list" v-if="activities.length > 0">
            <div v-for="(activity, index) in activities" :key="activity.id"
              class="item-row-wrap" :class="{ 'item-row-wrap--removing': deletingId === activity.id }">
            <div class="item-row"
              :class="{ 'item-row--drag-over': dragSrcType === 'activities' && dragOverItemIdx === index && dragSrcIdx !== index }"
              draggable="true"
              @dragstart="onItemDragStart(index, 'activities', $event)"
              @dragover.prevent="onItemDragOver(index, 'activities')"
              @drop.prevent="onItemDrop(index, 'activities')"
              @dragend="onItemDragEnd"
            >
              <div class="drag-handle" title="Arrastar para reordenar">
                <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
                  <circle cx="3" cy="3" r="1.4" fill="currentColor"/><circle cx="7" cy="3" r="1.4" fill="currentColor"/>
                  <circle cx="3" cy="7" r="1.4" fill="currentColor"/><circle cx="7" cy="7" r="1.4" fill="currentColor"/>
                  <circle cx="3" cy="11" r="1.4" fill="currentColor"/><circle cx="7" cy="11" r="1.4" fill="currentColor"/>
                </svg>
              </div>
              <div class="flip-card" :class="{ 'flip-card--flipped': deletePending?.type === 'activities' && deletePending.index === index }">
                <div class="flip-card-back">
                  <p class="flip-confirm-label">Excluir atividade {{ index + 1 }}?</p>
                  <div class="flip-confirm-actions">
                    <button class="flip-btn flip-btn--yes" type="button" @click="confirmDelete">Sim, excluir</button>
                    <button class="flip-btn flip-btn--no" type="button" @click="cancelDelete">Não</button>
                  </div>
                </div>
                <div class="flip-card-front expandable-item">
                <div class="expandable-item-header">
                  <div class="item-badge">{{ index + 1 }}</div>
                  <span class="item-title">Atividade {{ index + 1 }}</span>
                  <button v-if="canUseIA && activity.description.trim()" class="btn-ai-item" type="button"
                    :disabled="iaRewrite?.type === 'activities' && iaRewrite.index === index && iaRewrite.loading"
                    @click="triggerIaRewrite('activities', index)"
                  >
                    <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 1v2M7 11v2M1 7h2M11 7h2M2.8 2.8l1.4 1.4M9.8 9.8l1.4 1.4M2.8 11.2l1.4-1.4M9.8 4.2l1.4-1.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                    {{ iaRewrite?.type === 'activities' && iaRewrite.index === index && iaRewrite.loading ? 'Processando…' : 'Modo IA' }}
                  </button>
                  <button class="item-remove-btn" type="button" @click="askDelete('activities', index)" title="Excluir">
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 3.5h10M5.5 3.5V2.5h3V3.5M4.5 3.5v7.5h5V3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    Excluir
                  </button>
                </div>
                <div class="field" style="margin-bottom:14px;">
                  <label :for="`activity-${activity.id}`">Descrição</label>
                  <textarea :id="`activity-${activity.id}`" v-model="activity.description" class="item-textarea" placeholder="Descrição breve da atividade" rows="3" />
                  <div v-if="iaRewrite && iaRewrite.type === 'activities' && iaRewrite.index === index && !iaRewrite.loading" class="ia-suggestion">
                    <div class="ia-suggestion-label">
                      <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 1v2M7 11v2M1 7h2M11 7h2M2.8 2.8l1.4 1.4M9.8 9.8l1.4 1.4M2.8 11.2l1.4-1.4M9.8 4.2l1.4-1.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                      Sugestão da IA
                    </div>
                    <p class="ia-suggestion-text">{{ iaRewrite.result }}</p>
                    <div class="ia-suggestion-actions">
                      <button class="ia-btn ia-btn--accept" type="button" @click="acceptIaRewrite">Manter</button>
                      <button class="ia-btn ia-btn--dismiss" type="button" @click="dismissIaRewrite">Descartar</button>
                    </div>
                  </div>
                </div>
                <div class="field">
                  <label>Fotos <span class="img-count">{{ activity.images.length }}/25</span></label>
                  <div class="image-grid" :class="{ 'is-drag-over': dragOverActivityId === activity.id }"
                    @dragenter.prevent="onImageDragEnter(activity.id)"
                    @dragover.prevent="onImageDragEnter(activity.id)"
                    @dragleave.prevent="onImageDragLeave(activity.id)"
                    @drop.prevent="onImagesDropped(activity.id, $event)"
                  >
                    <div v-for="(img, imgIndex) in activity.images" :key="`${activity.id}-img-${imgIndex}`" class="image-preview">
                      <img :src="resolveUrl(img)" alt="preview" />
                      <button class="image-remove" type="button" @click="removeActivityImage(activity.id, imgIndex)" aria-label="Remover foto">
                        <span class="trash-icon" aria-hidden="true"></span>
                      </button>
                    </div>
                    <button v-if="activity.images.length < 25" class="image-add" type="button" @click="triggerImageInput(activity.id)">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 4v10M4 9h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                    </button>
                  </div>
                  <input :id="`file-${activity.id}`" class="hidden-input" type="file" accept="image/*" multiple @change="onImagesSelected(activity.id, $event)" />
                </div>
                </div><!-- /flip-card-front -->
              </div><!-- /flip-card -->
            </div><!-- /item-row -->
            </div><!-- /item-row-wrap -->
          </div>
          <button class="btn-add-bottom" type="button" @click="addActivity">
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 3v8M3 7h8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            Adicionar atividade
          </button>
        </div>

        <!-- ── 7. Observações ── -->
        <div class="sfv-section expandable-section">
          <div class="section-head">
            <div class="section-label">Observações</div>
            <button class="btn-inline" type="button" @click="addObservation">+ Adicionar</button>
          </div>
          <div class="expandable-list" v-if="observations.length > 0">
            <div v-for="(section, index) in observations" :key="`obs-${section.id}`"
              class="item-row-wrap" :class="{ 'item-row-wrap--removing': deletingId === section.id }">
            <div class="item-row"
              :class="{ 'item-row--drag-over': dragSrcType === 'observations' && dragOverItemIdx === index && dragSrcIdx !== index }"
              draggable="true"
              @dragstart="onItemDragStart(index, 'observations', $event)"
              @dragover.prevent="onItemDragOver(index, 'observations')"
              @drop.prevent="onItemDrop(index, 'observations')"
              @dragend="onItemDragEnd"
            >
              <div class="drag-handle" title="Arrastar para reordenar">
                <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
                  <circle cx="3" cy="3" r="1.4" fill="currentColor"/><circle cx="7" cy="3" r="1.4" fill="currentColor"/>
                  <circle cx="3" cy="7" r="1.4" fill="currentColor"/><circle cx="7" cy="7" r="1.4" fill="currentColor"/>
                  <circle cx="3" cy="11" r="1.4" fill="currentColor"/><circle cx="7" cy="11" r="1.4" fill="currentColor"/>
                </svg>
              </div>
              <div class="flip-card" :class="{ 'flip-card--flipped': deletePending?.type === 'observations' && deletePending.index === index }">
                <div class="flip-card-back">
                  <p class="flip-confirm-label">Remover observação {{ index + 1 }}?</p>
                  <div class="flip-confirm-actions">
                    <button class="flip-btn flip-btn--yes" type="button" @click="confirmDelete">Sim, remover</button>
                    <button class="flip-btn flip-btn--no" type="button" @click="cancelDelete">Não</button>
                  </div>
                </div>
                <div class="flip-card-front expandable-item">
                <div class="expandable-item-header">
                  <div class="item-badge">{{ index + 1 }}</div>
                  <span class="item-title">Observação {{ index + 1 }}</span>
                  <button v-if="canUseIA && observations[index].text.trim()" class="btn-ai-item" type="button"
                    :disabled="iaRewrite?.type === 'observations' && iaRewrite.index === index && iaRewrite.loading"
                    @click="triggerIaRewrite('observations', index)"
                  >
                    <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 1v2M7 11v2M1 7h2M11 7h2M2.8 2.8l1.4 1.4M9.8 9.8l1.4 1.4M2.8 11.2l1.4-1.4M9.8 4.2l1.4-1.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                    {{ iaRewrite?.type === 'observations' && iaRewrite.index === index && iaRewrite.loading ? 'Processando…' : 'Modo IA' }}
                  </button>
                  <button class="item-remove-btn" type="button" @click="askDelete('observations', index)" title="Remover">
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 3.5h10M5.5 3.5V2.5h3V3.5M4.5 3.5v7.5h5V3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    Remover
                  </button>
                </div>
                <div class="field" style="margin-bottom:14px;">
                  <label :for="`obs-text-${section.id}`">Texto</label>
                  <textarea :id="`obs-text-${section.id}`" v-model="observations[index].text" class="item-textarea" placeholder="Observação" rows="3" />
                  <!-- IA suggestion box -->
                  <div v-if="iaRewrite && iaRewrite.type === 'observations' && iaRewrite.index === index && !iaRewrite.loading" class="ia-suggestion">
                    <div class="ia-suggestion-label">
                      <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M7 1v2M7 11v2M1 7h2M11 7h2M2.8 2.8l1.4 1.4M9.8 9.8l1.4 1.4M2.8 11.2l1.4-1.4M9.8 4.2l1.4-1.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                      Sugestão da IA
                    </div>
                    <p class="ia-suggestion-text">{{ iaRewrite.result }}</p>
                    <div class="ia-suggestion-actions">
                      <button class="ia-btn ia-btn--accept" type="button" @click="acceptIaRewrite">Manter</button>
                      <button class="ia-btn ia-btn--dismiss" type="button" @click="dismissIaRewrite">Descartar</button>
                    </div>
                  </div>
                </div>
                <div class="field">
                  <label>Fotos <span class="img-count">{{ section.images.length }}/25</span></label>
                  <div class="image-grid" :class="{ 'is-drag-over': dragOverObsId === section.id }"
                    @dragenter.prevent="dragOverObsId = section.id"
                    @dragover.prevent="dragOverObsId = section.id"
                    @dragleave.prevent="dragOverObsId = dragOverObsId === section.id ? '' : dragOverObsId"
                    @drop.prevent="onObsImagesDropped(section.id, $event)"
                  >
                    <div v-for="(img, imgIndex) in section.images" :key="`obs-${section.id}-img-${imgIndex}`" class="image-preview">
                      <img :src="resolveUrl(img)" alt="preview" />
                      <button class="image-remove" type="button" @click="removeObsImage(section.id, imgIndex)" aria-label="Remover foto">
                        <span class="trash-icon" aria-hidden="true"></span>
                      </button>
                    </div>
                    <button v-if="section.images.length < 25" class="image-add" type="button" @click="triggerObsImageInput(section.id)">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 4v10M4 9h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                    </button>
                  </div>
                  <input :id="`file-obs-${section.id}`" class="hidden-input" type="file" accept="image/*" multiple @change="onObsImagesSelected(section.id, $event)" />
                </div>
                </div><!-- /flip-card-front -->
              </div><!-- /flip-card -->
            </div><!-- /item-row -->
            </div><!-- /item-row-wrap -->
          </div>
          <button class="btn-add-bottom" type="button" @click="addObservation">
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 3v8M3 7h8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            Adicionar observação
          </button>
        </div>

        <!-- ── 8. Requisição de Itens ── -->
        <div id="requisicao-section" class="sfv-section expandable-section">
          <div class="section-head">
            <div class="section-label">Requisição de Itens</div>
            <button class="btn-inline" type="button" @click="addRequestedItem">+ Adicionar</button>
          </div>
          <div v-if="requestedItems.length === 0" class="req-empty-hint">
            Faltou alguma ferramenta ou material? Adicione aqui os itens que precisam ser comprados — ao enviar o relatório, uma requisição de compra será criada automaticamente no Escritório.
          </div>
          <div class="expandable-list" v-if="requestedItems.length > 0">
            <div v-for="(reqItem, index) in requestedItems" :key="`req-${reqItem.id}`"
              class="item-row-wrap" :class="{ 'item-row-wrap--removing': deletingId === reqItem.id }">
            <div class="item-row">
              <div class="flip-card" :class="{ 'flip-card--flipped': deletePending?.type === 'requestedItems' && deletePending.index === index }">
                <div class="flip-card-back">
                  <p class="flip-confirm-label">Remover item {{ index + 1 }}?</p>
                  <div class="flip-confirm-actions">
                    <button class="flip-btn flip-btn--yes" type="button" @click="confirmDelete">Sim, remover</button>
                    <button class="flip-btn flip-btn--no" type="button" @click="cancelDelete">Não</button>
                  </div>
                </div>
                <div class="flip-card-front expandable-item">
                <div class="expandable-item-header">
                  <div class="item-badge">{{ index + 1 }}</div>
                  <span class="item-title">{{ reqItem.nome || `Item ${index + 1}` }}</span>
                  <button class="item-remove-btn" type="button" @click="askDelete('requestedItems', index)" title="Remover">
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 3.5h10M5.5 3.5V2.5h3V3.5M4.5 3.5v7.5h5V3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    Remover
                  </button>
                </div>
                <div class="field" style="margin-bottom:14px;">
                  <label :for="`req-nome-${reqItem.id}`">Nome do item *</label>
                  <input :id="`req-nome-${reqItem.id}`" v-model="reqItem.nome" type="text" placeholder="Ex.: Furadeira de impacto 220V" />
                </div>
                <div class="field" style="margin-bottom:14px;">
                  <label :for="`req-desc-${reqItem.id}`">Descrição</label>
                  <textarea :id="`req-desc-${reqItem.id}`" v-model="reqItem.descricao" class="item-textarea" placeholder="Detalhes do item (marca, modelo, especificações…)" rows="2" />
                </div>
                <div class="field" style="margin-bottom:14px;">
                  <label :for="`req-link-${reqItem.id}`">Link</label>
                  <input :id="`req-link-${reqItem.id}`" v-model="reqItem.link" type="url" placeholder="https://…" />
                </div>
                <div class="field">
                  <label>Fotos <span class="img-count">{{ reqItem.images.length }}/25</span></label>
                  <div class="image-grid" :class="{ 'is-drag-over': dragOverReqId === reqItem.id }"
                    @dragenter.prevent="dragOverReqId = reqItem.id"
                    @dragover.prevent="dragOverReqId = reqItem.id"
                    @dragleave.prevent="dragOverReqId = dragOverReqId === reqItem.id ? '' : dragOverReqId"
                    @drop.prevent="onReqImagesDropped(reqItem.id, $event)"
                  >
                    <div v-for="(img, imgIndex) in reqItem.images" :key="`req-${reqItem.id}-img-${imgIndex}`" class="image-preview">
                      <img :src="resolveUrl(img)" alt="preview" />
                      <button class="image-remove" type="button" @click="removeReqImage(reqItem.id, imgIndex)" aria-label="Remover foto">
                        <span class="trash-icon" aria-hidden="true"></span>
                      </button>
                    </div>
                    <button v-if="reqItem.images.length < 25" class="image-add" type="button" @click="triggerReqImageInput(reqItem.id)">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 4v10M4 9h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                    </button>
                  </div>
                  <input :id="`file-req-${reqItem.id}`" class="hidden-input" type="file" accept="image/*" multiple @change="onReqImagesSelected(reqItem.id, $event)" />
                </div>
                </div><!-- /flip-card-front -->
              </div><!-- /flip-card -->
            </div><!-- /item-row -->
            </div><!-- /item-row-wrap -->
          </div>
          <button class="btn-add-bottom" type="button" @click="addRequestedItem">
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 3v8M3 7h8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            Adicionar item
          </button>
        </div>

        <!-- ── Error ── -->
        <div v-if="error" class="sfv-error">
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="7.5" r="6" stroke="currentColor" stroke-width="1.3"/><path d="M7.5 5v3.5M7.5 10.5v.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          {{ error }}
        </div>

      </form>
    </div>

    <!-- ── Bottom bar ── -->
    <div class="sfv-bottom-bar">
      <div class="sfv-bb-left">
        <button class="sfv-bb-btn sfv-bb-btn--ghost" type="button" @click="cancel" :disabled="isSaving">
          Cancelar
        </button>
        <button v-if="isEdit" class="sfv-bb-btn sfv-bb-btn--danger" type="button" @click="remove" :disabled="isSaving">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 3.5h10M5.5 3.5V2.5h3V3.5M4.5 3.5v7.5h5V3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
          Excluir
        </button>
      </div>
      <div class="sfv-bb-right">
        <button class="sfv-bb-btn sfv-bb-btn--secondary" type="button" @click="saveDraft" :disabled="isSaving">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M3 7h8M7 3v8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
          Salvar rascunho
        </button>
        <button class="sfv-bb-btn sfv-bb-btn--primary" type="button" @click="send" :disabled="isSaving">
          Enviar relatório
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M5 3l5 4-5 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
    </div>

    <NoticeModal
      :open="noticeOpen"
      :title="noticeMessage"
      button-text="Entendi"
      @close="noticeOpen = false"
    />

    <!-- ── "Faltou alguma ferramenta?" prompt ── -->
    <transition name="autosave-toast">
      <div v-if="showToolRequestPrompt" class="tool-request-prompt">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
        <span class="tool-request-text">Faltou alguma ferramenta?</span>
        <button class="tool-request-yes" type="button" @click="acceptToolRequestPrompt">Sim</button>
        <button class="tool-request-close" type="button" @click="showToolRequestPrompt = false" aria-label="Fechar">
          <svg width="11" height="11" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2L2 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
        </button>
      </div>
    </transition>

    <!-- ── Autosave toast ── -->
    <transition name="autosave-toast">
      <div v-if="autoSaveStatus !== 'idle'" class="autosave-toast" :class="`autosave-toast--${autoSaveStatus}`">
        <template v-if="autoSaveStatus === 'saving'">
          <svg class="spin-icon" width="13" height="13" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-dasharray="40" stroke-dashoffset="15" stroke-linecap="round"/></svg>
          Salvando rascunho…
        </template>
        <template v-else-if="autoSaveStatus === 'saved'">
          <svg width="13" height="13" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          Rascunho salvo às {{ autoSaveTime }}
        </template>
        <template v-else-if="autoSaveStatus === 'error'">
          <svg width="13" height="13" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1.3"/><path d="M6 4v2.5M6 8.5v.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
          Erro ao salvar rascunho
        </template>
      </div>
    </transition>

  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { navigationStore } from '@/store/navigation'
import { sessionStore } from '@/store/session'
import { submissionsStore, type SubmissionActivity, type SubmissionSection, type SubmissionRequestedItem, type SubmissionReportLanguage } from '@/store/submissions'
import { newId } from '@/utils/id'
import { apiFetch } from '@/store/api'
import { projectsStore } from '@/store/projects'
import { accountsStore } from '@/store/accounts'
import { systemStore } from '@/store/system'
import { localeStore, SUPPORTED_LOCALES } from '@/store/locale'
import { machineryStore } from '@/store/machinery'
import { getTodayLocalISODate } from '@/utils/date'
import { resolveUrl } from '@/utils/resolveUrl'
import NoticeModal from '@/components/NoticeModal.vue'
import { uiStore } from '@/store/ui'

type ActivityDraft = SubmissionActivity
const MAX_IMAGES_PER_ACTIVITY = 25
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024 // aceita até 10MB, comprime depois
const TARGET_SIZE_BYTES = 200 * 1024          // alvo: 200KB
const MAX_DIMENSION = 1920                     // largura/altura máxima

function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const src = e.target?.result as string
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_DIMENSION) / width)
            width = MAX_DIMENSION
          } else {
            width = Math.round((width * MAX_DIMENSION) / height)
            height = MAX_DIMENSION
          }
        }
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) { reject(new Error('Canvas 2D context not available')); return }
        ctx.drawImage(img, 0, 0, width, height)

        // Comprime iterativamente até atingir TARGET_SIZE_BYTES
        let quality = 0.85
        let result = canvas.toDataURL('image/jpeg', quality)
        while (result.length * 0.75 > TARGET_SIZE_BYTES && quality > 0.2) {
          quality -= 0.1
          result = canvas.toDataURL('image/jpeg', quality)
        }
        resolve(result)
      }
      img.onerror = reject
      img.src = src
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export default defineComponent({
  name: 'SubmissionFormView',
  components: {
    NoticeModal
  },
  props: {
    embedded: {
      type: Boolean,
      default: false
    },
    submissionId: {
      type: String,
      default: ''
    },
    prefillType: {
      type: String,
      default: ''
    }
  },
  emits: ['done', 'cancel', 'edit'],
  setup(props, { emit }) {
    const route = useRoute()
    const router = useRouter()
    const title = ref('')
    const reportType = ref<'Relatorio diario de obra' | 'Relatorio de incidente' | 'Relatorio geral' | ''>('')
    const REPORT_LANGUAGES = SUPPORTED_LOCALES as unknown as { code: SubmissionReportLanguage; label: string; flag: string }[]
    const reportLanguage = ref<SubmissionReportLanguage>(localeStore.state.current as SubmissionReportLanguage)
    const attraction = ref('')
    const reportDate = ref(getTodayLocalISODate())
    const collaborators = ref<string[]>([])
    const coAuthors = ref<string[]>([])
    const tools = ref<SubmissionSection[]>([])
    const activities = ref<ActivityDraft[]>([])
    const observations = ref<SubmissionSection[]>([])
    const requestedItems = ref<SubmissionRequestedItem[]>([])
    const createdOnBehalfOfUsername = ref('')
    const error = ref('')
    const isSaving = ref(false)
    const isLoading = ref(true)
    const noticeOpen = ref(false)
    const noticeMessage = ref('')
    const dragOverActivityId = ref('')
    const dragOverToolId = ref('')
    const dragOverObsId = ref('')
    const dragOverReqId = ref('')
    const showToolRequestPrompt = ref(false)
    const typePickerOpen = ref(false)
    const showLangDropdown = ref(false)
    const langDropdownRef = ref<HTMLElement | null>(null)
    const suppressDuplicateWarning = ref(false)

    const reportTypeLabel = computed(() => {
      if (reportType.value === 'Relatorio diario de obra') return 'Relatório diário'
      if (reportType.value === 'Relatorio de incidente') return 'Relatório de incidente'
      if (reportType.value === 'Relatorio geral') return 'Relatório geral'
      return ''
    })

    const duplicateSubmission = computed(() => {
      if (isEdit.value || !reportType.value || !attraction.value || !reportDate.value) return null
      return submissionsStore.all().find(
        item =>
          item.title === reportType.value &&
          item.attraction === attraction.value &&
          item.reportDate === reportDate.value &&
          item.id !== autoSavedId.value
      ) || null
    })

    // ── Autosave state ────────────────────────────────────────────────
    interface FormSnapshot {
      title: string
      attraction: string
      reportDate: string
      collaborators: string[]
      coAuthors: string[]
      tools: SubmissionSection[]
      activities: ActivityDraft[]
      observations: SubmissionSection[]
      requestedItems: SubmissionRequestedItem[]
      reportLanguage: SubmissionReportLanguage
      createdOnBehalfOfUsername: string
    }

    const autoSavedId = ref('')
    const isAutoSaving = ref(false)
    const autoSaveStatus = ref<'idle' | 'saving' | 'saved' | 'error'>('idle')
    const autoSaveTime = ref('')
    const pendingChanges = ref(0)
    const lastSavedSnapshot = ref<FormSnapshot | null>(null)
    const isFormReady = ref(false)
    const AUTO_SAVE_INTERVAL_MS = 60_000
    const AUTO_SAVE_CHANGES_THRESHOLD = 5
    let autoSaveInterval: ReturnType<typeof setInterval> | null = null
    let changeDebounceTimer: ReturnType<typeof setTimeout> | null = null

    const projectOptions = computed(() => {
      return projectsStore.all().map((item) => item.name).filter(Boolean)
    })
    const isAdminUser = computed(() => sessionStore.state.user?.role === 'admin-master' || sessionStore.state.user?.role === 'admin-obra')
    const authorOptions = computed(() => {
      const rows = accountsStore.all()
      if (!rows.length) return []
      return rows
        .map((item) => ({ username: item.username, name: item.name || item.username }))
        .filter((item) => Boolean(item.username))
    })

    const currentId = computed(() => (props.submissionId ? props.submissionId : (route.params.id as string)))
    const isEdit = computed(() => !!currentId.value)

    const loadSubmission = () => {
      if (!isEdit.value) return
      const id = currentId.value
      const item = submissionsStore.getById(id)
      if (item) {
        title.value = item.title
        reportType.value =
          item.title === 'Relatorio diario de obra' || item.title === 'Relatorio de incidente' || item.title === 'Relatorio geral'
            ? (item.title as 'Relatorio diario de obra' | 'Relatorio de incidente' | 'Relatorio geral')
            : ''
        attraction.value = item.attraction
        reportDate.value = item.reportDate
        collaborators.value = [...item.collaborators]
        coAuthors.value = [...(item.coAuthors || [])]
        tools.value = item.tools.map(t => ({ id: t.id, text: t.text, images: [...t.images] }))
        activities.value = item.activities.map((activity) => ({
          id: activity.id,
          description: activity.description,
          images: [...activity.images]
        }))
        observations.value = item.observations.map(o => ({ id: o.id, text: o.text, images: [...o.images] }))
        requestedItems.value = (item.requestedItems || []).map(r => ({ id: r.id, nome: r.nome, descricao: r.descricao, link: r.link, images: [...r.images] }))
        reportLanguage.value = item.reportLanguage
      }
    }

    const applyQueryType = () => {
      if (isEdit.value) return
      const type = props.prefillType || (route.query.type as string | undefined)
      if (type === 'Relatorio diario de obra' || type === 'Relatorio de incidente' || type === 'Relatorio geral') {
        reportType.value = type
      }
    }

    const ensureProjectSelected = () => {
      if (isEdit.value) return
      if (attraction.value) return
      if (projectOptions.value.length > 0) {
        attraction.value = projectOptions.value[0]
      }
    }

    const createActivity = () => ({
      id: newId(),
      description: '',
      images: []
    })

    const cloneActivity = (activity: ActivityDraft): ActivityDraft => ({
      id: newId(),
      description: activity.description || '',
      images: [...(activity.images || [])]
    })

    const addCollaborator = () => collaborators.value.push('')
    const removeCollaborator = (index: number) => collaborators.value.splice(index, 1)

    // ── Co-author picker ──────────────────────────────────────────────
    const coAuthorPickerOpen = ref(false)
    const coAuthorSearch = ref('')
    const coAuthorPickerRef = ref<HTMLElement | null>(null)

    const coAuthorPickerOptions = computed(() => {
      const currentUsername = sessionStore.state.user?.username || ''
      return authorOptions.value.filter(
        u => u.username !== currentUsername && !coAuthors.value.includes(u.username)
      )
    })

    const filteredCoAuthorOptions = computed(() => {
      const q = coAuthorSearch.value.trim().toLowerCase()
      if (!q) return coAuthorPickerOptions.value
      return coAuthorPickerOptions.value.filter(
        u => u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q)
      )
    })

    const resolveCoAuthorName = (username: string) =>
      authorOptions.value.find(u => u.username === username)?.name || username

    const addCoAuthor = (username: string) => {
      if (!coAuthors.value.includes(username)) coAuthors.value.push(username)
      coAuthorPickerOpen.value = false
      coAuthorSearch.value = ''
    }
    const removeCoAuthor = (username: string) => {
      const i = coAuthors.value.indexOf(username)
      if (i >= 0) coAuthors.value.splice(i, 1)
    }

    const handleCoAuthorPickerClickOutside = (e: MouseEvent) => {
      if (coAuthorPickerRef.value && !coAuthorPickerRef.value.contains(e.target as Node)) {
        coAuthorPickerOpen.value = false
      }
    }

    watch(coAuthorPickerOpen, (val) => {
      if (val) {
        nextTick(() => document.addEventListener('mousedown', handleCoAuthorPickerClickOutside))
      } else {
        document.removeEventListener('mousedown', handleCoAuthorPickerClickOutside)
        coAuthorSearch.value = ''
      }
    })

    const createSection = (): SubmissionSection => ({
      id: newId(),
      text: '',
      images: []
    })

    const machineryOptions = computed(() => machineryStore.state.machinery.filter(m => m.status !== 'inativo'))
    const getMachineTitle = (id: string) => {
      const m = machineryStore.state.machinery.find(m => m.id === id)
      return m ? m.title : id
    }

    const addTool = () => tools.value.push(createSection())
    const removeTool = async (index: number) => {
      const label = getMachineTitle(tools.value[index]?.text || '') || `Maquinário ${index + 1}`
      if (!await uiStore.confirm(`Remover "${label}" da lista?`)) return
      tools.value.splice(index, 1)
    }

    const addObservation = () => observations.value.push(createSection())
    const removeObservation = async (index: number) => {
      if (!await uiStore.confirm(`Remover observação ${index + 1}?`)) return
      observations.value.splice(index, 1)
    }

    // ── Generic image processor for section arrays ─────────────────────
    const processImagesForSection = (arr: { id: string; images: string[] }[], sectionId: string, files: File[]) => {
      const section = arr.find(s => s.id === sectionId)
      if (!section) return
      const availableSlots = MAX_IMAGES_PER_ACTIVITY - section.images.length
      if (availableSlots <= 0) return
      const toProcess = files.slice(0, availableSlots)
      const oversized = toProcess.filter(f => !f.type.startsWith('image/') || f.size > MAX_IMAGE_SIZE_BYTES)
      const validFiles = toProcess.filter(f => f.type.startsWith('image/') && f.size <= MAX_IMAGE_SIZE_BYTES)
      if (oversized.length) {
        noticeMessage.value = `${oversized.length} arquivo(s) ignorado(s): apenas imagens até 10MB são aceitas.`
        noticeOpen.value = true
      }
      validFiles.forEach(async (file) => {
        try {
          const compressed = await compressImage(file)
          section.images.push(compressed)
        } catch {
          noticeMessage.value = 'Erro ao processar uma imagem.'
          noticeOpen.value = true
        }
      })
    }

    // ── Tool image handlers ────────────────────────────────────────────
    const removeToolImage = (id: string, idx: number) => {
      const s = tools.value.find(t => t.id === id)
      if (s) s.images.splice(idx, 1)
    }
    const triggerToolImageInput = (id: string) => {
      const input = document.getElementById(`file-tool-${id}`) as HTMLInputElement | null
      if (input) input.click()
    }
    const onToolImagesSelected = (id: string, event: Event) => {
      const target = event.target as HTMLInputElement
      processImagesForSection(tools.value, id, Array.from(target.files || []))
      target.value = ''
    }
    const onToolImagesDropped = (id: string, event: DragEvent) => {
      dragOverToolId.value = ''
      processImagesForSection(tools.value, id, Array.from(event.dataTransfer?.files || []))
    }

    // ── Observation image handlers ─────────────────────────────────────
    const removeObsImage = (id: string, idx: number) => {
      const s = observations.value.find(o => o.id === id)
      if (s) s.images.splice(idx, 1)
    }
    const triggerObsImageInput = (id: string) => {
      const input = document.getElementById(`file-obs-${id}`) as HTMLInputElement | null
      if (input) input.click()
    }
    const onObsImagesSelected = (id: string, event: Event) => {
      const target = event.target as HTMLInputElement
      processImagesForSection(observations.value, id, Array.from(target.files || []))
      target.value = ''
    }
    const onObsImagesDropped = (id: string, event: DragEvent) => {
      dragOverObsId.value = ''
      processImagesForSection(observations.value, id, Array.from(event.dataTransfer?.files || []))
    }

    // ── Requisição de Itens ────────────────────────────────────────────
    const createRequestedItem = (): SubmissionRequestedItem => ({
      id: newId(),
      nome: '',
      descricao: '',
      link: '',
      images: []
    })
    const addRequestedItem = () => requestedItems.value.push(createRequestedItem())
    const acceptToolRequestPrompt = () => {
      showToolRequestPrompt.value = false
      if (requestedItems.value.length === 0) addRequestedItem()
      nextTick(() => {
        document.getElementById('requisicao-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
    const removeReqImage = (id: string, idx: number) => {
      const r = requestedItems.value.find(item => item.id === id)
      if (r) r.images.splice(idx, 1)
    }
    const triggerReqImageInput = (id: string) => {
      const input = document.getElementById(`file-req-${id}`) as HTMLInputElement | null
      if (input) input.click()
    }
    const onReqImagesSelected = (id: string, event: Event) => {
      const target = event.target as HTMLInputElement
      processImagesForSection(requestedItems.value, id, Array.from(target.files || []))
      target.value = ''
    }
    const onReqImagesDropped = (id: string, event: DragEvent) => {
      dragOverReqId.value = ''
      processImagesForSection(requestedItems.value, id, Array.from(event.dataTransfer?.files || []))
    }

    const addActivity = () => activities.value.push(createActivity())
    const removeActivity = async (index: number) => {
      const desc = activities.value[index]?.description || `Atividade ${index + 1}`
      if (!await uiStore.confirm(`Excluir "${desc}"?`)) return
      activities.value.splice(index, 1)
    }
    const removeActivityImage = (activityId: string, imageIndex: number) => {
      const activity = activities.value.find((item) => item.id === activityId)
      if (!activity) return
      activity.images.splice(imageIndex, 1)
    }

    const triggerImageInput = (activityId: string) => {
      const input = document.getElementById(`file-${activityId}`) as HTMLInputElement | null
      if (input) input.click()
    }

    const processSelectedImages = (activityId: string, files: File[]) => {
      if (!files.length) return
      const activity = activities.value.find((item) => item.id === activityId)
      if (!activity) return

      const availableSlots = MAX_IMAGES_PER_ACTIVITY - activity.images.length
      if (availableSlots <= 0) return

      const toProcess = files.slice(0, availableSlots)
      const oversized = toProcess.filter((file) => !file.type.startsWith('image/') || file.size > MAX_IMAGE_SIZE_BYTES)
      const validFiles = toProcess.filter((file) => file.type.startsWith('image/') && file.size <= MAX_IMAGE_SIZE_BYTES)

      if (oversized.length) {
        noticeMessage.value = `${oversized.length} arquivo(s) ignorado(s): apenas imagens até 10MB são aceitas.`
        noticeOpen.value = true
      }

      validFiles.forEach(async (file) => {
        try {
          const compressed = await compressImage(file)
          activity.images.push(compressed)
        } catch {
          noticeMessage.value = 'Erro ao processar uma imagem.'
          noticeOpen.value = true
        }
      })
    }

    const onImagesSelected = (activityId: string, event: Event) => {
      const target = event.target as HTMLInputElement
      const files = target.files ? Array.from(target.files) : []
      processSelectedImages(activityId, files)
      target.value = ''
    }

    const onImageDragEnter = (activityId: string) => {
      dragOverActivityId.value = activityId
    }

    const onImageDragLeave = (activityId: string) => {
      if (dragOverActivityId.value === activityId) {
        dragOverActivityId.value = ''
      }
    }

    const onImagesDropped = (activityId: string, event: DragEvent) => {
      dragOverActivityId.value = ''
      const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : []
      processSelectedImages(activityId, files)
    }

    const loadSupervisorCollaborators = () => {
      if (isEdit.value) return
      if (!attraction.value) return
      if (collaborators.value.length > 0) return
      const last = submissionsStore.latestSupervisorByAttraction(attraction.value)
      if (last && last.collaborators.length > 0) {
        collaborators.value = [...last.collaborators]
      }
    }

    const copyFromLatestDailyReport = () => {
      if (!attraction.value) {
        noticeMessage.value = 'Selecione um projeto antes de copiar os dados.'
        noticeOpen.value = true
        return
      }
      const latest = submissionsStore
        .all()
        .filter((item) => item.title === 'Relatorio diario de obra' && item.attraction === attraction.value)
        .sort((a, b) => {
          if (a.reportDate === b.reportDate) return a.createdAt < b.createdAt ? 1 : -1
          return a.reportDate < b.reportDate ? 1 : -1
        })[0]

      if (!latest) {
        noticeMessage.value = 'Nao foi encontrado relatorio diario anterior para este projeto.'
        noticeOpen.value = true
        return
      }

      collaborators.value = [...latest.collaborators]
      tools.value = latest.tools.map(t => ({ ...createSection(), text: t.text, images: [...t.images] }))
      activities.value = latest.activities.map((activity) => cloneActivity(activity))
      observations.value = latest.observations.map(o => ({ ...createSection(), text: o.text, images: [...o.images] }))
      error.value = ''
    }

    const normalizeList = (items: string[]) => items.map((item) => item.trim()).filter(Boolean)

    const captureCurrentSnapshot = (): FormSnapshot => ({
      title: reportType.value,
      attraction: attraction.value,
      reportDate: reportDate.value,
      collaborators: normalizeList(collaborators.value),
      coAuthors: normalizeList(coAuthors.value),
      tools: tools.value
        .map(t => ({ id: t.id, text: t.text.trim(), images: t.images }))
        .filter(t => t.text || t.images.length > 0),
      activities: activities.value.map(a => ({
        id: a.id,
        description: a.description.trim(),
        images: a.images
      })),
      observations: observations.value
        .map(o => ({ id: o.id, text: o.text.trim(), images: o.images }))
        .filter(o => o.text || o.images.length > 0),
      requestedItems: requestedItems.value
        .map(r => ({ id: r.id, nome: r.nome.trim(), descricao: r.descricao.trim(), link: r.link.trim(), images: r.images }))
        .filter(r => r.nome || r.descricao || r.link || r.images.length > 0),
      reportLanguage: reportLanguage.value,
      createdOnBehalfOfUsername: isAdminUser.value && !isEdit.value
        ? (createdOnBehalfOfUsername.value || (sessionStore.state.user?.username ?? ''))
        : ''
    })

    const computeDelta = (current: FormSnapshot, previous: FormSnapshot): Partial<FormSnapshot> => {
      const delta: Partial<FormSnapshot> = {}
      for (const key of Object.keys(current) as (keyof FormSnapshot)[]) {
        if (JSON.stringify(current[key]) !== JSON.stringify(previous[key])) {
          (delta as Record<string, unknown>)[key] = current[key]
        }
      }
      return delta
    }

    const buildPayload = (status: 'rascunho' | 'enviado') => ({
      ...captureCurrentSnapshot(),
      category: 'relatorio' as const,
      status
    })

    const autoSaveDraft = async () => {
      if (isAutoSaving.value || isSaving.value) return
      if (!reportType.value || !attraction.value) return

      const user = sessionStore.state.user
      if (!user) return

      const currentSnapshot = captureCurrentSnapshot()
      const effectiveId = isEdit.value ? currentId.value : autoSavedId.value

      // If an existing record exists and we have a baseline, only proceed if something changed
      if (effectiveId && lastSavedSnapshot.value) {
        const delta = computeDelta(currentSnapshot, lastSavedSnapshot.value)
        if (Object.keys(delta).length === 0) {
          pendingChanges.value = 0
          return
        }
      }

      isAutoSaving.value = true
      autoSaveStatus.value = 'saving'

      try {
        if (effectiveId) {
          // Send only what changed; fall back to full snapshot on first save of an edit
          const changed = lastSavedSnapshot.value
            ? computeDelta(currentSnapshot, lastSavedSnapshot.value)
            : { ...currentSnapshot }
          await submissionsStore.updateSubmission(effectiveId, { ...changed, status: 'rascunho' })
        } else {
          const createdId = await submissionsStore.addSubmission({
            ...currentSnapshot,
            category: 'relatorio' as const,
            status: 'rascunho',
            author: user
          })
          autoSavedId.value = createdId
        }

        lastSavedSnapshot.value = currentSnapshot
        const now = new Date()
        autoSaveTime.value = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        autoSaveStatus.value = 'saved'
        pendingChanges.value = 0
        setTimeout(() => { if (autoSaveStatus.value === 'saved') autoSaveStatus.value = 'idle' }, 4000)
      } catch {
        autoSaveStatus.value = 'error'
      } finally {
        isAutoSaving.value = false
      }
    }

    const onFormChanged = () => {
      if (!isFormReady.value) return
      if (changeDebounceTimer) clearTimeout(changeDebounceTimer)
      changeDebounceTimer = setTimeout(() => {
        pendingChanges.value++
        if (pendingChanges.value >= AUTO_SAVE_CHANGES_THRESHOLD) {
          autoSaveDraft()
        }
      }, 1000)
    }

    const persist = async (status: 'rascunho' | 'enviado') => {
      if (isSaving.value) return
      error.value = ''
      if (!reportType.value) {
        error.value = 'Selecione o tipo de relatorio.'
        return
      }
      if (!attraction.value) {
        error.value = 'Selecione um projeto.'
        return
      }
      const incompleteReqItem = requestedItems.value.find(
        r => !r.nome.trim() && (r.descricao.trim() || r.link.trim() || r.images.length > 0)
      )
      if (incompleteReqItem) {
        error.value = 'Informe o nome do item na Requisição de Itens (campo obrigatório).'
        return
      }
      const user = sessionStore.state.user
      if (!user) {
        router.push('/login')
        return
      }
      const basePayload = buildPayload(status)

      const selectedAuthor = authorOptions.value.find((item) => item.username === createdOnBehalfOfUsername.value)
      const effectiveAuthor = isAdminUser.value && !isEdit.value && selectedAuthor
        ? { ...user, username: selectedAuthor.username, name: selectedAuthor.name }
        : user

      // effective edit ID: real edit route OR a draft already autosaved
      const effectiveId = isEdit.value ? currentId.value : autoSavedId.value

      try {
        if (status === 'enviado') {
          isSaving.value = true
        }
        if (effectiveId) {
          await submissionsStore.updateSubmission(effectiveId, basePayload)
        } else {
          await submissionsStore.addSubmission({ ...basePayload, author: effectiveAuthor })
        }
        if (props.embedded) {
          emit('done')
        } else {
          router.push('/home')
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Falha ao salvar.'
        if (message.includes('Sem permissao para enviar submissao neste projeto nesta data')) {
          noticeMessage.value = 'Nao foi possivel enviar: voce nao e responsavel pelo projeto selecionado.'
          noticeOpen.value = true
          return
        }
        error.value = message
      } finally {
        isSaving.value = false
      }
    }

    const saveDraft = () => persist('rascunho')
    const send = () => persist('enviado')
    const cancel = () => {
      if (props.embedded) {
        emit('cancel')
      } else {
        navigationStore.goBack(router, '/submissions')
      }
    }

    const remove = async () => {
      if (!currentId.value) return
      if (!await uiStore.confirm('Excluir esta submissao?')) return
      await submissionsStore.deleteSubmission(currentId.value)
      if (props.embedded) {
        emit('done')
      } else {
        router.push('/home')
      }
    }

    const editExistingReport = () => {
      if (!duplicateSubmission.value) return
      if (props.embedded) {
        emit('edit', duplicateSubmission.value.id)
      } else {
        router.push(`/submission/${duplicateSubmission.value.id}/edit`)
      }
    }

    const handleOutsideClick = (e: MouseEvent) => {
      if (langDropdownRef.value && !langDropdownRef.value.contains(e.target as Node)) {
        showLangDropdown.value = false
      }
    }

    onMounted(async () => {
      window.addEventListener('click', handleOutsideClick)
      try {
        await projectsStore.refresh().catch(() => undefined)
        if (isAdminUser.value) {
          await accountsStore.refresh().catch(() => undefined)
        }
        await submissionsStore.refresh().catch(() => undefined)
        await machineryStore.loadMachinery().catch(() => undefined)
        createdOnBehalfOfUsername.value = sessionStore.state.user?.username || ''
        ensureProjectSelected()
        loadSubmission()
        loadSupervisorCollaborators()
        applyQueryType()
        lastSavedSnapshot.value = captureCurrentSnapshot()
      } finally {
        isLoading.value = false
      }

      autoSaveInterval = setInterval(autoSaveDraft, AUTO_SAVE_INTERVAL_MS)
      isFormReady.value = true

      // Notificação "Faltou alguma ferramenta?" (só se ainda não há itens solicitados)
      if (requestedItems.value.length === 0) {
        setTimeout(() => {
          if (requestedItems.value.length === 0) showToolRequestPrompt.value = true
        }, 1200)
      }
    })

    onUnmounted(() => {
      window.removeEventListener('click', handleOutsideClick)
      document.removeEventListener('mousedown', handleCoAuthorPickerClickOutside)
      if (autoSaveInterval) clearInterval(autoSaveInterval)
      if (changeDebounceTimer) clearTimeout(changeDebounceTimer)
    })

    watch(attraction, () => {
      loadSupervisorCollaborators()
      suppressDuplicateWarning.value = false
    })

    watch([reportType, reportDate], () => {
      suppressDuplicateWarning.value = false
    })

    watch(
      [reportType, attraction, reportDate, reportLanguage, collaborators, coAuthors, tools, activities, observations, requestedItems],
      onFormChanged,
      { deep: true }
    )

    // ── Delete flip-card confirmation ────────────────────────────────────────
    type DeletableListType = 'tools' | 'activities' | 'observations' | 'requestedItems'
    const deletePending = ref<{ type: DeletableListType; index: number } | null>(null)
    const deletingId = ref('')

    function askDelete(type: DeletableListType, index: number) {
      deletePending.value = { type, index }
    }

    async function confirmDelete() {
      if (!deletePending.value) return
      const { type, index } = deletePending.value
      const list = type === 'tools' ? tools : type === 'activities' ? activities : type === 'requestedItems' ? requestedItems : observations
      const item = list.value[index]
      deletePending.value = null
      deletingId.value = item.id
      await new Promise(r => setTimeout(r, 340))
      const idx = list.value.findIndex(i => i.id === deletingId.value)
      if (idx !== -1) list.value.splice(idx, 1)
      deletingId.value = ''
    }

    function cancelDelete() {
      deletePending.value = null
    }

    // ── Drag-to-reorder ──────────────────────────────────────────────────────
    const dragSrcType = ref<'tools' | 'activities' | 'observations' | ''>('')
    const dragSrcIdx = ref(-1)
    const dragOverItemIdx = ref(-1)

    function onItemDragStart(index: number, type: 'tools' | 'activities' | 'observations', e: DragEvent) {
      dragSrcType.value = type
      dragSrcIdx.value = index
      if (!e.dataTransfer) return
      e.dataTransfer.effectAllowed = 'move'

      // Build a clean read-only ghost card
      const ghost = document.createElement('div')
      Object.assign(ghost.style, {
        position: 'fixed', top: '-9999px', left: '0',
        background: '#f5f4f1', border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '10px', padding: '12px 16px', width: '280px',
        boxShadow: '0 4px 18px rgba(0,0,0,0.14)', fontFamily: 'inherit',
        pointerEvents: 'none',
      })

      if (type === 'activities') {
        const act = activities.value[index]
        const desc = act.description ? `<div style="font-size:13px;color:#333;margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${act.description.slice(0, 70)}${act.description.length > 70 ? '…' : ''}</div>` : ''
        const photos = act.images.length ? `<div style="font-size:11px;color:#999;margin-top:6px;">${act.images.length} foto(s)</div>` : ''
        ghost.innerHTML = `<div style="font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.05em;">Atividade ${index + 1}</div>${desc}${photos}`
      } else if (type === 'tools') {
        const tool = tools.value[index]
        const name = getMachineTitle(tool.text) || (tool.text ? tool.text : '—')
        ghost.innerHTML = `<div style="font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.05em;">Maquinário ${index + 1}</div><div style="font-size:13px;color:#333;margin-top:4px;">${name}</div>`
      } else {
        const obs = observations.value[index]
        const txt = obs.text ? `<div style="font-size:13px;color:#333;margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${obs.text.slice(0, 70)}${obs.text.length > 70 ? '…' : ''}</div>` : ''
        const photos = obs.images.length ? `<div style="font-size:11px;color:#999;margin-top:6px;">${obs.images.length} foto(s)</div>` : ''
        ghost.innerHTML = `<div style="font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.05em;">Observação ${index + 1}</div>${txt}${photos}`
      }

      document.body.appendChild(ghost)
      e.dataTransfer.setDragImage(ghost, 24, 24)
      setTimeout(() => { if (document.body.contains(ghost)) document.body.removeChild(ghost) }, 100)
    }

    function onItemDragOver(index: number, type: 'tools' | 'activities' | 'observations') {
      if (dragSrcType.value !== type) return
      dragOverItemIdx.value = index
    }

    function onItemDrop(targetIdx: number, type: 'tools' | 'activities' | 'observations') {
      if (dragSrcType.value !== type) return
      const src = dragSrcIdx.value
      if (src === targetIdx) return
      const list = type === 'tools' ? tools : type === 'activities' ? activities : observations
      const [moved] = list.value.splice(src, 1)
      list.value.splice(targetIdx, 0, moved)
      dragSrcType.value = ''
      dragSrcIdx.value = -1
      dragOverItemIdx.value = -1
    }

    function onItemDragEnd() {
      dragSrcType.value = ''
      dragSrcIdx.value = -1
      dragOverItemIdx.value = -1
    }

    // ── Modo IA por item ─────────────────────────────────────────────────────
    type IaTarget = { type: 'tools' | 'activities' | 'observations'; index: number; loading: boolean; result: string }
    const iaRewrite = ref<IaTarget | null>(null)
    const canUseIA = computed(() => systemStore.state.featureFlags['ia'] !== false)

    async function triggerIaRewrite(type: 'tools' | 'activities' | 'observations', index: number) {
      const list = type === 'tools' ? tools : type === 'activities' ? activities : observations
      const item = list.value[index]
      const rawText = type === 'activities' ? (item as SubmissionActivity).description
        : type === 'tools' ? (item as SubmissionSection).text
        : (item as SubmissionSection).text
      if (!rawText || !rawText.trim()) return
      const contextLabel = type === 'activities' ? 'atividade executada em obra' : type === 'tools' ? 'maquinário utilizado' : 'observação de obra'
      iaRewrite.value = { type, index, loading: true, result: '' }
      try {
        const data = await apiFetch<{ result: string }>('/ia/rewrite', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: rawText.trim(), context: contextLabel })
        })
        if (iaRewrite.value && iaRewrite.value.type === type && iaRewrite.value.index === index) {
          iaRewrite.value = { type, index, loading: false, result: data.result }
        }
      } catch {
        iaRewrite.value = null
      }
    }

    function acceptIaRewrite() {
      if (!iaRewrite.value || iaRewrite.value.loading) return
      const { type, index, result } = iaRewrite.value
      const list = type === 'tools' ? tools : type === 'activities' ? activities : observations
      const item = list.value[index]
      if (type === 'activities') (item as SubmissionActivity).description = result
      else (item as SubmissionSection).text = result
      iaRewrite.value = null
    }

    function dismissIaRewrite() {
      iaRewrite.value = null
    }

    return {
      REPORT_LANGUAGES,
      typePickerOpen,
      showLangDropdown,
      langDropdownRef,
      title,
      reportType,
      reportLanguage,
      attraction,
      reportDate,
      collaborators,
      coAuthors,
      addCoAuthor,
      removeCoAuthor,
      coAuthorPickerOpen,
      coAuthorPickerRef,
      coAuthorSearch,
      coAuthorPickerOptions,
      filteredCoAuthorOptions,
      resolveCoAuthorName,
      tools,
      activities,
      observations,
      requestedItems,
      addRequestedItem,
      removeReqImage,
      triggerReqImageInput,
      onReqImagesSelected,
      onReqImagesDropped,
      showToolRequestPrompt,
      acceptToolRequestPrompt,
      error,
      isSaving,
      noticeOpen,
      noticeMessage,
      dragOverActivityId,
      dragOverToolId,
      dragOverObsId,
      dragOverReqId,
      projectOptions,
      isAdminUser,
      createdOnBehalfOfUsername,
      authorOptions,
      isEdit,
      machineryOptions,
      getMachineTitle,
      addCollaborator,
      removeCollaborator,
      addTool,
      removeTool,
      removeToolImage,
      triggerToolImageInput,
      onToolImagesSelected,
      onToolImagesDropped,
      addActivity,
      removeActivity,
      removeActivityImage,
      addObservation,
      removeObservation,
      removeObsImage,
      triggerObsImageInput,
      onObsImagesSelected,
      onObsImagesDropped,
      copyFromLatestDailyReport,
      triggerImageInput,
      onImagesSelected,
      onImageDragEnter,
      onImageDragLeave,
      onImagesDropped,
      saveDraft,
      send,
      remove,
      cancel,
      resolveUrl,
      isLoading,
      autoSaveStatus,
      autoSaveTime,
      isAutoSaving,
      duplicateSubmission,
      suppressDuplicateWarning,
      reportTypeLabel,
      editExistingReport,
      deletePending,
      deletingId,
      askDelete,
      confirmDelete,
      cancelDelete,
      dragSrcType,
      dragSrcIdx,
      dragOverItemIdx,
      onItemDragStart,
      onItemDragOver,
      onItemDrop,
      onItemDragEnd,
      canUseIA,
      iaRewrite,
      triggerIaRewrite,
      acceptIaRewrite,
      dismissIaRewrite,
      getInitials: (name: string) => {
        if (!name) return '?'
        const parts = name.trim().split(/\s+/)
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
    }
  }
})
</script>

<style scoped lang="scss">
/* ─── Page shell ───────────────────────────────── */
.sfv-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background: var(--bg, #f4f3f0);
}

/* ─── Top bar ──────────────────────────────────── */
.sfv-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 52px;
  background: var(--card, #fff);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
  gap: 12px;
}

.sfv-topbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.sfv-topbar-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: var(--ink-muted, #888);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.12s, background 0.12s;
  &:hover { color: var(--ink, #2c2b27); background: rgba(0,0,0,0.04); }
}

.sfv-topbar-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink, #2c2b27);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  span { color: var(--ink-muted, #888); font-weight: 400; }
}

.sfv-topbar-esc {
  background: none;
  border: none;
  color: var(--ink-muted, #aaa);
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.12s;
  &:hover { color: var(--ink, #2c2b27); }
}

/* ─── Body ─────────────────────────────────────── */
.sfv-body {
  flex: 1;
  min-height: 0;
  padding: 24px 20px;
  overflow-y: auto;
}

.sfv-page.embedded .sfv-body {
  padding: 18px 16px;
}

.sfv-shell {
  width: 100%;
  max-width: 860px;
  margin: 0 auto;
}

.sfv-page.embedded .sfv-shell {
  max-width: none;
}

/* ─── Form layout ──────────────────────────────── */
.sfv-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ─── Sections ─────────────────────────────────── */
.sfv-section {
  background: var(--card, #fff);
  border-radius: 14px;
  padding: 20px 22px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  &.no-mb { margin-bottom: 10px; }
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--ink-muted, #999);
}

.section-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ─── Type section: collapsible trigger ─────────── */
.type-section { padding: 0; }

.type-picker-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 22px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: var(--ink, #2c2b27);
  text-align: left;
  border-radius: 14px;
  transition: background 0.13s;

  &:hover { background: rgba(0, 0, 0, 0.025); }
}

.type-picker-label {
  strong { margin-left: 4px; color: var(--accent, #2563eb); }
}

.type-picker-chevron {
  flex-shrink: 0;
  color: var(--ink-muted, #aaa);
  transition: transform 0.2s;
  &.open { transform: rotate(180deg); }
}

.type-grid-wrap {
  padding: 0 22px 18px;
  overflow: hidden;
}

/* Slide-down animation */
.type-expand-enter-active { transition: max-height 0.25s ease, opacity 0.2s ease; max-height: 300px; }
.type-expand-leave-active { transition: max-height 0.2s ease, opacity 0.18s ease; }
.type-expand-enter-from  { max-height: 0; opacity: 0; }
.type-expand-leave-to    { max-height: 0; opacity: 0; }

/* ─── Type grid ─────────────────────────────────── */
.type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.type-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  background: var(--bg, #f4f3f0);
  cursor: pointer;
  transition: border-color 0.14s, background 0.14s, box-shadow 0.14s;
  user-select: none;

  &:hover { border-color: var(--accent, #2563eb); }

  &.active {
    border-color: var(--accent, #2563eb);
    background: rgba(37, 99, 235, 0.05);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);

    .type-check { opacity: 1; transform: scale(1); }
    .type-icon { color: var(--accent, #2563eb); }
    .type-name { color: var(--accent, #2563eb); }
  }
}

.type-check {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--accent, #2563eb);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 0.15s, transform 0.15s;
}

.type-icon {
  color: var(--ink-muted, #aaa);
  transition: color 0.14s;
  flex-shrink: 0;
}

.type-icon--incident { color: #ea580c; }
.type-card.active .type-icon--incident { color: #ea580c; }

.type-icon--general { color: #0d9488; }
.type-card.active .type-icon--general { color: #0d9488; }

.type-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink, #2c2b27);
  transition: color 0.14s;
}

.type-desc {
  font-size: 11.5px;
  color: var(--ink-muted, #aaa);
  line-height: 1.4;
}

@media (max-width: 560px) {
  .type-grid { grid-template-columns: 1fr; }
}

/* ─── Fields ────────────────────────────────────── */
.fields-row-3 {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr;
  gap: 12px;
}

@media (max-width: 640px) {
  .fields-row-3 { grid-template-columns: 1fr; }
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;

  label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-muted, #888);
  }

  input, select {
    height: 38px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: var(--bg, #f4f3f0);
    padding: 0 10px;
    font-size: 13px;
    color: var(--ink, #2c2b27);
    outline: none;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.14s;
    &:focus { border-color: var(--accent, #2563eb); }
  }
}

/* ─── Alert ─────────────────────────────────────── */
.alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 12.5px;
}

.alert-warn {
  background: rgba(216, 59, 1, 0.06);
  border: 1px solid rgba(216, 59, 1, 0.28);
  color: #b33000;

  strong { font-weight: 700; }
}

.alert-icon {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(216, 59, 1, 0.15);
  color: #d83b01;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
}

.alert-body { flex: 1; line-height: 1.5; }

.alert-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.alert-btn {
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid rgba(216, 59, 1, 0.35);
  background: transparent;
  color: #b33000;
  white-space: nowrap;
  transition: background 0.12s;
  &:hover { background: rgba(216, 59, 1, 0.08); }

  &--fill {
    background: #d83b01;
    border-color: #d83b01;
    color: #fff;
    &:hover { background: #c03300; }
  }
}

/* ─── Copy banner ────────────────────────────────── */
.copy-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 10px;
  background: rgba(37, 99, 235, 0.05);
  border: 1px dashed rgba(37, 99, 235, 0.28);
  font-size: 12.5px;
  color: var(--ink-muted, #888);

  svg { flex-shrink: 0; color: var(--accent, #2563eb); opacity: 0.7; }
  span { flex: 1; }
}

.copy-banner-btn {
  background: var(--accent, #2563eb);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.14s;
  &:hover { opacity: 0.88; }
}

/* ─── List section (Colaboradores / Co-autores) ── */
.list-section { }

.list-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.list-item-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--accent, #2563eb);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.03em;
}

.list-item-input {
  flex: 1;
  height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: var(--bg, #f4f3f0);
  padding: 0 12px;
  font-size: 13px;
  color: var(--ink, #2c2b27);
  outline: none;
  transition: border-color 0.14s;
  &:focus { border-color: var(--accent, #2563eb); }
}

.list-item-remove {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  border: 1px solid rgba(220, 38, 38, 0.25);
  background: transparent;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.12s;
  &:hover { background: rgba(220, 38, 38, 0.07); }
}

/* ─── Empty state ────────────────────────────────── */
.empty-state {
  font-size: 13px;
  color: var(--ink-muted, #bbb);
  padding: 4px 0 2px;
}

/* ─── Inline buttons ─────────────────────────────── */
.btn-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 11px;
  border-radius: 6px;
  border: 1px solid rgba(37, 99, 235, 0.3);
  background: rgba(37, 99, 235, 0.05);
  color: var(--accent, #2563eb);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s;
  &:hover { background: rgba(37, 99, 235, 0.1); }
}

/* ─── Modo IA button ─────────────────────────────── */
.btn-ai {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 6px;
  border: 1px solid rgba(109, 40, 217, 0.25);
  background: rgba(109, 40, 217, 0.06);
  color: #7c3aed;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s;
  svg { opacity: 0.8; }
  &:hover { background: rgba(109, 40, 217, 0.11); }
}

/* ─── Co-author picker ──────────────────────────── */
.coauthor-area {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-height: 34px;
}

.coauthor-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 10px;
  border-radius: 20px;
  background: rgba(37, 99, 235, 0.08);
  border: 1px solid rgba(37, 99, 235, 0.2);
  color: var(--accent, #2563eb);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1;

  svg { opacity: 0.6; flex-shrink: 0; }
}

.coauthor-chip-rm {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  background: transparent;
  color: currentColor;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0.6;
  padding: 0;
  transition: opacity 0.12s, background 0.12s;

  &:hover { opacity: 1; background: rgba(0,0,0,0.08); }
}

.coauthor-picker-wrap {
  position: relative;
}

.coauthor-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 200;
  background: var(--card, #fff);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  width: 240px;
  overflow: hidden;
}

.coauthor-search {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  background: transparent;
  font-size: 13px;
  color: var(--ink, #2c2b27);
  outline: none;

  &::placeholder { color: var(--ink-muted, #aaa); }
}

.coauthor-dropdown-list {
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: thin;
}

.coauthor-dropdown-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
  gap: 1px;

  &:hover { background: rgba(37, 99, 235, 0.06); }
}

.coauthor-dropdown-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink, #2c2b27);
}

.coauthor-dropdown-user {
  font-size: 11px;
  color: var(--ink-muted, #aaa);
}

.coauthor-dropdown-empty {
  padding: 12px;
  font-size: 13px;
  color: var(--ink-muted, #aaa);
  text-align: center;
}

/* ─── Language dropdown ────────────────────────── */
.lang-select-wrap {
  position: relative;
}

.lang-select-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 38px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: var(--bg, #faf9f6);
  cursor: pointer;
  font-size: 13px;
  color: var(--ink, #2c2b27);
  text-align: left;
  transition: border-color 0.14s;
  &:hover { border-color: var(--accent, #2563eb); }
}

.lang-select-label { flex: 1; font-weight: 500; }

.lang-select-chevron {
  flex-shrink: 0;
  color: var(--ink-muted, #aaa);
  transition: transform 0.18s;
  &.open { transform: rotate(180deg); }
}

.lang-select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  background: var(--card, #fff);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 4px;
  max-height: 260px;
  overflow-y: auto;
}

.lang-select-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 7px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: var(--ink, #2c2b27);
  text-align: left;
  transition: background 0.1s;

  &:hover { background: rgba(0, 0, 0, 0.04); }
  &.active { background: rgba(37, 99, 235, 0.07); color: var(--accent, #2563eb); font-weight: 600; }
}

.lang-check { margin-left: auto; flex-shrink: 0; color: var(--accent, #2563eb); }
.lang-btn-flag { font-size: 16px; line-height: 1; flex-shrink: 0; }

/* ─── Expandable sections ────────────────────────── */
.expandable-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
}

.expandable-item {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 14px 16px;
  background: var(--bg, #f4f3f0);
}

.expandable-item-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.item-badge {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--accent, #2563eb);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.item-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink, #2c2b27);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-remove-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: 1px solid rgba(220, 38, 38, 0.22);
  border-radius: 6px;
  color: #dc2626;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.12s;
  &:hover { background: rgba(220, 38, 38, 0.06); }
}

.btn-add-bottom {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 9px 14px;
  border-radius: 9px;
  border: 1.5px dashed rgba(0, 0, 0, 0.15);
  background: transparent;
  color: var(--ink-muted, #999);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
  &:hover {
    border-color: var(--accent, #2563eb);
    color: var(--accent, #2563eb);
    background: rgba(37, 99, 235, 0.03);
  }
}

.img-count {
  font-size: 11px;
  font-weight: 500;
  color: var(--ink-muted, #aaa);
  margin-left: 6px;
}

/* ─── Image grid ───────────────────────────────── */
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  padding: 8px;
  border: 1.5px dashed rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  min-height: 60px;
}

.image-grid.is-drag-over {
  border-color: var(--accent, #2563eb);
  background: rgba(37, 99, 235, 0.04);
}

.image-preview {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(31, 30, 26, 0.1);
  aspect-ratio: 1 / 1;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: filter 0.22s ease, transform 0.22s ease;
}

.image-remove {
  position: absolute;
  inset: 0;
  border: none;
  background: transparent;
  display: grid;
  place-items: center;
  opacity: 0;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.trash-icon {
  width: 28px;
  height: 28px;
  background: #fff;
  mask-image: url('@/assets/trash.svg');
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  -webkit-mask-image: url('@/assets/trash.svg');
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
}

.image-preview:hover img {
  filter: grayscale(100%) brightness(0.7);
  transform: scale(1.04);
}

.image-preview:hover .image-remove {
  opacity: 1;
}

.image-add {
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  border: 1.5px dashed rgba(37, 99, 235, 0.3);
  background: rgba(37, 99, 235, 0.04);
  color: var(--accent, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.14s, border-color 0.14s;
  min-height: 60px;
  &:hover {
    background: rgba(37, 99, 235, 0.09);
    border-color: var(--accent, #2563eb);
  }
}

/* ─── Error ────────────────────────────────────── */
.sfv-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(220, 38, 38, 0.07);
  border: 1px solid rgba(220, 38, 38, 0.2);
  color: #dc2626;
  font-size: 13px;
  font-weight: 500;
}

/* ─── Bottom bar ─────────────────────────────────── */
.sfv-bottom-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height: 52px;
  padding: 0 20px;
  background: var(--card, #fff);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.06);
}

/* ─── Autosave toast ───────────────────────────── */
/* ─── "Faltou alguma ferramenta?" prompt ────────── */
.tool-request-prompt {
  position: fixed;
  bottom: 76px;
  right: 24px;
  z-index: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px 10px 16px;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 600;
  background: var(--card, #fff);
  color: var(--ink, #2c2b27);
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.16);

  svg:first-child { color: var(--accent, #2563eb); flex-shrink: 0; }
}

.tool-request-text { white-space: nowrap; }

.tool-request-yes {
  border: none;
  background: var(--accent, #2563eb);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 16px;
  border-radius: 16px;
  cursor: pointer;
  transition: opacity 0.12s;
  &:hover { opacity: 0.88; }
}

.tool-request-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: var(--ink-muted, #999);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.12s, color 0.12s;
  &:hover { background: rgba(0, 0, 0, 0.06); color: var(--ink, #2c2b27); }
}

/* ─── Requisição de Itens ───────────────────────── */
.req-empty-hint {
  font-size: 12.5px;
  color: var(--ink-muted, #999);
  line-height: 1.5;
  padding: 2px 0 4px;
}

.autosave-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 800;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  border-radius: 24px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  pointer-events: none;

  &--saving {
    background: var(--card, #fff);
    color: var(--accent, #2563eb);
    border: 1px solid rgba(37, 99, 235, 0.2);
  }

  &--saved {
    background: var(--card, #fff);
    color: #16a34a;
    border: 1px solid rgba(22, 163, 74, 0.2);
  }

  &--error {
    background: var(--card, #fff);
    color: #dc2626;
    border: 1px solid rgba(220, 38, 38, 0.2);
  }
}

.autosave-toast-enter-active,
.autosave-toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.autosave-toast-enter-from,
.autosave-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.sfv-bb-left,
.sfv-bb-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ─── Bottom bar buttons ─────────────────────────── */
.sfv-bb-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: opacity 0.14s, background 0.14s;
  white-space: nowrap;

  &:disabled { opacity: 0.5; cursor: not-allowed; }

  &--primary {
    background: var(--accent, #2563eb);
    color: #fff;
    border-color: var(--accent, #2563eb);
    &:hover:not(:disabled) { opacity: 0.88; }
  }

  &--secondary {
    background: transparent;
    color: var(--accent, #2563eb);
    border-color: var(--accent, #2563eb);
    &:hover:not(:disabled) { background: rgba(37, 99, 235, 0.07); }
  }

  &--ghost {
    background: transparent;
    color: var(--ink-muted, #888);
    border-color: rgba(0, 0, 0, 0.15);
    &:hover:not(:disabled) { background: rgba(0, 0, 0, 0.04); }
  }

  &--danger {
    background: transparent;
    color: #dc2626;
    border-color: rgba(220, 38, 38, 0.35);
    &:hover:not(:disabled) { background: rgba(220, 38, 38, 0.07); }
  }
}

@media (max-width: 560px) {
  .sfv-bottom-bar {
    height: auto;
    min-height: 52px;
    padding: 8px 12px;
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    gap: 8px;
  }
  .sfv-bb-left { flex-shrink: 0; gap: 6px; }
  .sfv-bb-right { flex-shrink: 0; gap: 6px; }
  .sfv-bb-btn { white-space: nowrap; font-size: 12px; padding: 6px 12px; }

  .sfv-context-bar {
    overflow: hidden;
    flex-wrap: nowrap;
  }
  .sfv-context-project {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  .sfv-context-date { display: none; }
  .sfv-context-sep:last-of-type { display: none; }
}

/* ─── Saving overlay ───────────────────────────── */
.saving-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.4);
  display: grid;
  place-items: center;
  z-index: 999;
}

.saving-card {
  background: var(--card, #fff);
  padding: 16px 28px;
  border-radius: 14px;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

.spin-icon {
  animation: spin 1s linear infinite;
  color: var(--accent, #2563eb);
}

@keyframes spin { to { transform: rotate(360deg); } }

.sr-only {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  white-space: nowrap;
  border: 0;
}

.hidden-input { display: none; }

/* ─── Flip card (delete confirmation) ──────────────── */
.flip-card {
  position: relative;
  flex: 1;
  min-width: 0;
  transform-style: preserve-3d;
  transition: transform 0.38s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 0;
}

.flip-card--flipped {
  transform: rotateY(180deg);
}

.flip-card-front,
.flip-card-back {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  border-radius: 12px;
}

.flip-card-front {
  /* inherits .expandable-item styles */
}

.flip-card-back {
  position: absolute;
  inset: 0;
  transform: rotateY(180deg);
  background: #dc2626;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
}

.flip-confirm-label {
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  text-align: center;
}

.flip-confirm-actions {
  display: flex;
  gap: 10px;
}

.flip-btn {
  padding: 8px 22px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 2px solid transparent;
  transition: opacity 0.13s;

  &:hover { opacity: 0.85; }

  &--yes {
    background: #fff;
    color: #dc2626;
    border-color: #fff;
  }

  &--no {
    background: transparent;
    color: #fff;
    border-color: rgba(255,255,255,0.5);
  }
}

/* ─── Context bar (embedded mode) ─────────────────── */
.sfv-context-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  height: 52px;
  padding: 0 20px;
  font-size: 13px;
  color: var(--ink-muted, #777);
  background: var(--card, #fff);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  svg { flex-shrink: 0; opacity: 0.6; }
}

.sfv-context-sep { opacity: 0.45; font-size: 14px; }

.sfv-context-type { font-weight: 600; color: var(--ink, #2c2b27); }

.sfv-context-project {
  font-weight: 500;
  color: var(--ink, #2c2b27);
  opacity: 0.8;
}

.sfv-context-date { opacity: 0.7; }

/* ─── Drag-to-reorder rows ─────────────────────────── */
/* Wrapper handles vertical collapse only — no overflow clipping so width stays full */
.item-row-wrap {
  overflow: hidden;
  max-height: 2000px;
  opacity: 1;
  transition: max-height 0.34s ease, opacity 0.26s ease, margin 0.34s ease;

  &.item-row-wrap--removing {
    max-height: 0;
    opacity: 0;
    margin: 0 !important;
    pointer-events: none;
  }
}

.item-row {
  display: flex;
  align-items: stretch;
  gap: 0;
  position: relative;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.15s;

  &.item-row--drag-over {
    box-shadow: 0 0 0 2px var(--accent, #2563eb);
  }
}

/* Remove individual border from .expandable-item when inside item-row */
.item-row .expandable-item {
  border: none;
  border-radius: 0;
  flex: 1;
  min-width: 0;
}

.drag-handle {
  flex-shrink: 0;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.04);
  border-right: 1px solid rgba(0, 0, 0, 0.08);
  color: var(--ink-muted, #bbb);
  cursor: grab;
  transition: background 0.15s, color 0.15s;
  user-select: none;
  border-top: none;
  border-bottom: none;
  border-left: none;

  &:hover { background: rgba(0, 0, 0, 0.08); color: var(--accent, #2563eb); }
  &:active { cursor: grabbing; }
}

/* ─── Field label row (label + Modo IA button) ──────── */
.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;

  label { margin-bottom: 0 !important; }
}

/* ─── Per-item Modo IA button ───────────────────────── */
.btn-ai-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid rgba(124, 58, 237, 0.25);
  background: transparent;
  color: #7c3aed;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.12s, opacity 0.12s;

  svg { flex-shrink: 0; }
  &:hover:not(:disabled) { background: rgba(124, 58, 237, 0.06); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

/* ─── Textarea for item descriptions ─────────────────── */
.item-textarea {
  width: 100%;
  min-height: 72px;
  resize: vertical;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: var(--card, #fff);
  padding: 9px 12px;
  font-size: 13px;
  color: var(--ink, #2c2b27);
  font-family: inherit;
  line-height: 1.5;
  outline: none;
  transition: border-color 0.14s;
  box-sizing: border-box;
  display: block;

  &:focus { border-color: var(--accent, #2563eb); }
  &::placeholder { color: var(--ink-muted, #aaa); }
}

/* ─── IA Suggestion box ───────────────────────────────── */
.ia-suggestion {
  margin-top: 8px;
  border-radius: 10px;
  border: 1.5px solid rgba(147, 51, 234, 0.2);
  background: rgba(147, 51, 234, 0.04);
  padding: 10px 14px;
}

.ia-suggestion-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  color: #7c3aed;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 7px;
}

.ia-suggestion-text {
  font-size: 13px;
  color: var(--ink, #2c2b27);
  line-height: 1.55;
  margin: 0 0 10px;
  white-space: pre-wrap;
}

.ia-suggestion-actions {
  display: flex;
  gap: 8px;
}

.ia-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 14px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: opacity 0.13s, background 0.13s;

  &--accept {
    background: #7c3aed;
    color: #fff;
    border-color: #7c3aed;
    &:hover { opacity: 0.87; }
  }

  &--dismiss {
    background: transparent;
    color: var(--ink-muted, #888);
    border-color: rgba(0, 0, 0, 0.15);
    &:hover { background: rgba(0, 0, 0, 0.04); }
  }
}


</style>
